import React, { useMemo, useState } from 'react'
import { Modal, Steps, App, Button, Typography, Card } from 'antd'
import { CheckCircleTwoTone } from '@ant-design/icons'
import { useIntl } from 'react-intl'
import IntlMessages from '../../../util/IntlMessages'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { selectAuthCompanySlice, setAuthCompany } from '../../../store/auth-company-slice'
import Api from '@vacationtracker/shared/services/api'
import { WelcomeStep } from './steps/WelcomeStep'
import { MatchUsersStep } from './steps/MatchUsersStep'
import { openChat } from '../../../util/set-crisp-session-info'

const { Title, Paragraph, Text } = Typography

interface OnboardingADPConnectorProps {
  visible: boolean
  completedPercentage: number
  matchedUsersCount?: number
}

export const OnboardingADPConnector: React.FC<OnboardingADPConnectorProps> = ({ visible, completedPercentage }: OnboardingADPConnectorProps) => {
  const { notification } = App.useApp()
  const { formatMessage } = useIntl()
  const dispatch = useAppDispatch()
  const { authCompany } = useAppSelector(selectAuthCompanySlice)

  const steps = useMemo(
    () => [
      {
        title: <IntlMessages id="onboarding.adp.steps.welcome" />,
        key: 'welcome',
        completedPercentage: 10,
      },
      {
        title: <IntlMessages id="onboarding.adpConnector.steps.match" />,
        key: 'match',
        completedPercentage: 60,
      },
      {
        title: <IntlMessages id="onboarding.adp.summary.title" />,
        key: 'success',
        completedPercentage: 100,
      },
    ],
    []
  )

  const getInitialStep = () => {
    if (completedPercentage >= 100) return steps.length - 1
    for (let i = 0; i < steps.length; i++) {
      if (completedPercentage < steps[i].completedPercentage) return i
    }
    return 0
  }

  const [currentStep, setCurrentStep] = useState(getInitialStep())
  const [isLaunching, setIsLaunching] = useState(false)
  const [isFading, setIsFading] = useState(false)

  const sendProgressUpdateEvent = async (newPercentage: number) => {
    try {
      const event = {
        eventType: 'COMPANY_SETTINGS_UPDATED',
        eventGroup: 'COMPANY',
        companyId: authCompany?.id,
        name: authCompany?.name,
        contactEmail: authCompany?.contactEmail,
        userEndDate: authCompany?.userEndDate,
        integrationOnboardings: authCompany?.integrationOnboardings?.map((onboarding) => ({
          ...onboarding,
          completedPercentage: onboarding.name === 'ADP-CONNECTOR' ? newPercentage : onboarding.completedPercentage,
        })),
      }
      await Api.post('/core/event', event)
    } catch (error) {
      notification.error({
        message: formatMessage({ id: 'connect.platformErrorTitle' }),
        description: formatMessage({ id: 'connect.platformErrorDescription' }),
        duration: 10,
      })
      throw error
    }
  }

  const handleNext = async () => {
    if (currentStep < steps.length - 1) {
      const nextStep = currentStep + 1
      const completedStepPercentage = steps[currentStep].completedPercentage
      setCurrentStep(nextStep)
      await sendProgressUpdateEvent(completedStepPercentage)
    }
  }

  const handleLaunch = async () => {
    setIsFading(true)
    await sendProgressUpdateEvent(100)

    setTimeout(() => {
      authCompany &&
        dispatch(
          setAuthCompany({
            ...authCompany,
            integrationOnboardings: undefined,
          })
        )
      setIsLaunching(true)
    }, 800)
  }

  const handleConfirmMatching = async (matched: { vtId: string; adpId: string }[]) => {
    try {
      const event = {
        eventType: 'SYNC_INTEGRATION_USERS',
        eventGroup: 'BULK_ACTIONS',
        integrationType: 'ADP',
        users: matched,
      }
      await Api.post('/core/event', event)
      await handleNext()
    } catch (error) {
      notification.error({
        message: formatMessage({ id: 'notifications.error' }),
        description: formatMessage({ id: 'onboarding.adp.usersStep.importFailed' }),
      })
    }
  }

  const SuccessStep: React.FC<{ onLaunch: () => void }> = ({ onLaunch }) => {
    return (
      <div className="summary-step-container">
        <Title level={2} className="summary-step-title">
          <IntlMessages id="onboarding.adp.summaryStep.title" />
          <span className="summary-step-party-emoji" role="img" aria-label="party">
            🎉
          </span>
        </Title>
        <Paragraph className="summary-step-description">
          <IntlMessages id="onboarding.adp.summaryStep.subtitle" />
        </Paragraph>
        <Card className="summary-step-success-box">
          <Title level={4} className="summary-step-success-title">
            <IntlMessages id="onboarding.adp.summaryStep.whatWeSetup" />
          </Title>
          <ul className="summary-step-checklist">
            <li>
              <CheckCircleTwoTone twoToneColor="#52c41a" className="summary-step-check-icon" />
              <Text>
                <IntlMessages id="onboarding.adpConnector.success.usersSynced" />
              </Text>
            </li>
            <li>
              <CheckCircleTwoTone twoToneColor="#52c41a" className="summary-step-check-icon" />
              <Text>
                <IntlMessages id="onboarding.adp.summaryStep.approvalWorkflows" />
              </Text>
            </li>
          </ul>
        </Card>
        <Card className="summary-step-tip-box">
          <span className="summary-step-tip-emoji" role="img" aria-label="bulb">
            💡
          </span>
          <Text>
            <IntlMessages id="onboarding.adp.summaryStep.proTip" />
          </Text>
          <Button onClick={() => openChat()} type="link" className="summary-step-tip-action">
            <IntlMessages id="onboarding.adp.summaryStep.scheduleCall" />
          </Button>
        </Card>
        <Button type="primary" size="large" className="summary-step-launch-btn" onClick={onLaunch}>
          <IntlMessages id="onboarding.adp.summaryStep.launchButton" />
        </Button>
      </div>
    )
  }

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return <WelcomeStep onNext={() => void handleNext()} />
      case 1:
        return (
          <MatchUsersStep
            onConfirm={(ids) => {
              void handleConfirmMatching(ids)
            }}
          />
        )
      case 2:
        return <SuccessStep onLaunch={() => void handleLaunch()} />
      default:
        return null
    }
  }

  return (
    <Modal
      title={null}
      open={visible && !isLaunching}
      footer={null}
      closable={false}
      maskClosable={false}
      keyboard={false}
      destroyOnHidden={false}
      width={1200}
      centered
      className={`onboarding-adp-wizard ${isFading ? 'onboarding-adp-wizard-fading' : ''}`}
    >
      <div className="onboarding-adp-wizard-container">
        <div className="onboarding-adp-wizard-step-indicator">
          <Steps
            current={currentStep}
            size="small"
            items={steps.map((step, index) => ({
              title: <span className={index === currentStep ? 'step-title-current' : index < currentStep ? 'step-title-completed' : 'step-title-pending'}>{step.title}</span>,
              status: index === currentStep ? 'process' : index < currentStep ? 'finish' : 'wait',
            }))}
          />
        </div>
        <div className="onboarding-adp-wizard-step-content">{renderStepContent()}</div>
      </div>
    </Modal>
  )
}

export default OnboardingADPConnector
